// 設定画面: 音声エンジン切替・音量・速度・APIキー
import { useState } from 'react';
import { AppSettings, SpeechEngine } from '../types';
import { loadSettings, saveSettings } from '../lib/storage';
import { speak } from '../lib/speech';

export default function Settings() {
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings());
  const [saved, setSaved] = useState(false);

  const update = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    saveSettings(next);
    setSaved(true);
    setTimeout(() => setSaved(false), 1200);
  };

  const handleTest = async () => {
    try {
      await speak('テスト読み上げです。聞こえていますか？', settings);
    } catch (err) {
      alert(`再生失敗: ${err}`);
    }
  };

  const engines: { value: SpeechEngine; label: string; desc: string; cost: string; status: string }[] = [
    {
      value: 'web-speech',
      label: 'ブラウザ標準（Web Speech API）',
      desc: '無料で即動作。機械的な声。MVPで体験する分には十分。',
      cost: '無料',
      status: '✅ ローカルでも本番でも即動作',
    },
    {
      value: 'openai-tts',
      label: 'OpenAI TTS（自然な声）',
      desc: '自然で聞き取りやすい声。ai-proxy経由なので個別APIキー不要。',
      cost: '約$0.015/1000文字',
      status: '🚧 Cloudflareデプロイ + 環境変数設定後に利用可能（未設定時は自動でブラウザ標準にフォールバック）',
    },
    {
      value: 'elevenlabs',
      label: 'ElevenLabs（声クローン対応）',
      desc: '親の声をクローンして読み上げ可能。最も人間らしい。',
      cost: '月$5〜（要APIキー）',
      status: '🚧 APIキー登録後に利用可能（未設定時は自動でブラウザ標準にフォールバック）',
    },
  ];

  return (
    <>
      <div className="card">
        <h2>音声エンジン</h2>
        <p className="description">3種類から選べます。初回はブラウザ標準（無料）でお試しください。</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {engines.map((e) => (
            <label
              key={e.value}
              style={{
                display: 'flex',
                gap: 12,
                padding: 12,
                border: `2px solid ${settings.speechEngine === e.value ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderRadius: 10,
                cursor: 'pointer',
                background: settings.speechEngine === e.value ? '#fff3eb' : 'white',
              }}
            >
              <input
                type="radio"
                name="engine"
                checked={settings.speechEngine === e.value}
                onChange={() => update('speechEngine', e.value)}
                style={{ marginTop: 4 }}
              />
              <div>
                <div style={{ fontWeight: 600 }}>{e.label}</div>
                <div className="muted" style={{ marginTop: 2 }}>{e.desc}</div>
                <div className="muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>料金: {e.cost}</div>
                <div style={{ fontSize: '0.8rem', marginTop: 4, color: e.value === 'web-speech' ? 'var(--color-success)' : 'var(--color-warning)' }}>
                  {e.status}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {settings.speechEngine === 'openai-tts' && (
        <div className="card">
          <h2>OpenAI TTS 接続設定</h2>
          <p className="description">
            ai-proxy経由のPages Functionのベースを指定。空の場合は同一オリジン `/api/tts` を使用。
          </p>
          <div className="form-row">
            <label>APIベース（オプション）</label>
            <input
              type="text"
              value={settings.apiBase || ''}
              onChange={(e) => update('apiBase', e.target.value)}
              placeholder="https://kids-task-voice.pages.dev"
            />
          </div>
        </div>
      )}

      {settings.speechEngine === 'elevenlabs' && (
        <div className="card">
          <h2>ElevenLabs 接続設定</h2>
          <div className="alert">
            ⚠️ APIキーはこの端末のlocalStorageに保存されます。共有端末では使わないでください。
          </div>
          <div className="form-row">
            <label>APIキー</label>
            <input
              type="password"
              value={settings.elevenLabsApiKey || ''}
              onChange={(e) => update('elevenLabsApiKey', e.target.value)}
              placeholder="xi-..."
            />
          </div>
          <div className="form-row">
            <label>Voice ID（空の場合は既定 Rachel）</label>
            <input
              type="text"
              value={settings.elevenLabsVoiceId || ''}
              onChange={(e) => update('elevenLabsVoiceId', e.target.value)}
              placeholder="21m00Tcm4TlvDq8ikWAM"
            />
          </div>
        </div>
      )}

      <div className="card">
        <h2>音量・速度</h2>
        <div className="form-row">
          <label>音量 ({Math.round(settings.volume * 100)}%)</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={settings.volume}
            onChange={(e) => update('volume', parseFloat(e.target.value))}
          />
        </div>
        <div className="form-row">
          <label>読み上げ速度 ({settings.rate.toFixed(2)}x)</label>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={settings.rate}
            onChange={(e) => update('rate', parseFloat(e.target.value))}
          />
        </div>
        <button className="btn btn-primary" onClick={handleTest}>
          🔊 テスト読み上げ
        </button>
      </div>

      {saved && (
        <div className="status-banner" style={{ position: 'fixed', bottom: 24, right: 24, width: 'auto' }}>
          ✓ 保存しました
        </div>
      )}
    </>
  );
}
